import React, { useEffect, useMemo, useState } from "react";
import { Alert, Avatar, Box, Button, Card, CardContent, Chip, Grid, Stack, Tab, Tabs, Typography } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { useTranslation } from "react-i18next";
import HourglassTopOutlinedIcon from "@mui/icons-material/HourglassTopOutlined";
import AddCircleOutlineOutlinedIcon from "@mui/icons-material/AddCircleOutlineOutlined";
import MarkEmailReadOutlinedIcon from "@mui/icons-material/MarkEmailReadOutlined";
import PaidOutlinedIcon from "@mui/icons-material/PaidOutlined";
import LocalOfferOutlinedIcon from "@mui/icons-material/LocalOfferOutlined";
import {
  getStorePayouts,
  getStorePayoutHistory,
  sendStorePayout,
} from "../../api/deliveryCompanyService";
import { formatCurrency, formatDate } from "../Admin/constants";

const VIEW_TABS = [
  { value: "requests", labelKey: "dcStoreFees:tabRequests" },
  { value: "awaiting", labelKey: "dcShared:sentAwaitingConfirmation" },
  { value: "history", labelKey: "dcShared:history" },
];

function StatCard({ icon: Icon, label, value, color }) {
  return (
    <Card sx={{ height: "100%" }}>
      <CardContent>
        <Stack direction="row" alignItems="center" spacing={2}>
          <Avatar sx={{ bgcolor: `${color}.main`, width: 48, height: 48 }}>
            <Icon />
          </Avatar>
          <Box>
            <Typography variant="body2" color="text.secondary">
              {label}
            </Typography>
            <Typography variant="h5">{value}</Typography>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
}

export default function StoreFees() {
  const { t } = useTranslation(["dcStoreFees", "dcShared"]);
  const [view, setView] = useState("requests");
  const [rows, setRows] = useState([]);
  const [historyRows, setHistoryRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busyStoreId, setBusyStoreId] = useState(null);

  function loadRows() {
    setLoading(true);
    Promise.all([getStorePayouts(), getStorePayoutHistory()])
      .then(([payouts, history]) => {
        setRows(payouts);
        setHistoryRows(history);
        setError("");
      })
      .catch(() => setError("dcStoreFees:loadError"))
      .finally(() => setLoading(false));
  }

  useEffect(loadRows, []);

  async function handleSend(row) {
    setBusyStoreId(row.storeId);
    try {
      await sendStorePayout(row.storeId);
      loadRows();
    } catch {
      setError("dcStoreFees:sendError");
    } finally {
      setBusyStoreId(null);
    }
  }

  const requestRows = useMemo(
    () => rows.filter((r) => r.requestedAmount > 0 || r.pendingAmount > 0),
    [rows]
  );

  // StorePayoutStatus: Sent = 1, Received = 2 (serialized as the numeric enum value)
  const awaitingRows = useMemo(() => historyRows.filter((r) => r.status === 1), [historyRows]);
  const completedRows = useMemo(() => historyRows.filter((r) => r.status === 2), [historyRows]);

  const totals = useMemo(
    () =>
      rows.reduce(
        (acc, r) => ({
          requested: acc.requested + (r.requestedAmount || 0),
          newPending: acc.newPending + (r.pendingAmount || 0),
          awaitingConfirmation: acc.awaitingConfirmation + (r.awaitingConfirmationAmount || 0),
          totalPaid: acc.totalPaid + (r.totalPaid || 0),
          discountGiven: acc.discountGiven + (r.requestedDiscountAmount || 0) + (r.pendingDiscountAmount || 0),
        }),
        { requested: 0, newPending: 0, awaitingConfirmation: 0, totalPaid: 0, discountGiven: 0 }
      ),
    [rows]
  );

  const requestColumns = useMemo(
    () => [
      { field: "storeName", headerName: t("dcShared:store"), flex: 1, minWidth: 180 },
      {
        field: "hasOpenRequest",
        headerName: t("dcStoreFees:requestedByOwner"),
        width: 190,
        sortable: false,
        renderCell: (params) =>
          params.row.hasOpenRequest ? (
            <Box sx={{ py: 1 }}>
              <Chip size="small" color="warning" label={t("dcStoreFees:requested")} />
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                {formatDate(params.row.requestedAt)}
              </Typography>
            </Box>
          ) : (
            <Typography variant="caption" color="text.secondary">
              {t("dcStoreFees:notRequestedYet")}
            </Typography>
          ),
      },
      {
        field: "requestedAmount",
        headerName: t("dcStoreFees:requestedAmount"),
        width: 160,
        valueFormatter: (v) => (v > 0 ? formatCurrency(v) : "—"),
      },
      { field: "requestedOrderCount", headerName: t("dcShared:orders"), width: 90 },
      {
        field: "requestedDiscountAmount",
        headerName: t("dcShared:couponDiscount"),
        width: 150,
        valueFormatter: (v) => (v > 0 ? formatCurrency(v) : "—"),
      },
      {
        field: "pendingAmount",
        headerName: t("dcStoreFees:newNotRequested"),
        width: 190,
        valueFormatter: (v) => (v > 0 ? formatCurrency(v) : "—"),
      },
      { field: "pendingOrderCount", headerName: t("dcShared:orders"), width: 90 },
      {
        field: "pendingDiscountAmount",
        headerName: t("dcShared:couponDiscount"),
        width: 150,
        valueFormatter: (v) => (v > 0 ? formatCurrency(v) : "—"),
      },
      {
        field: "send",
        headerName: "",
        width: 190,
        sortable: false,
        renderCell: (params) => {
          const label = params.row.hasOpenRequest ? t("dcStoreFees:sendRequestedPayout") : t("dcStoreFees:sendPayout");
          return (
            <Button
              size="small"
              variant="contained"
              disabled={busyStoreId === params.row.storeId}
              onClick={() => handleSend(params.row)}
            >
              {label}
            </Button>
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [busyStoreId, t]
  );

  const awaitingColumns = useMemo(
    () => [
      { field: "storeName", headerName: t("dcShared:store"), flex: 1, minWidth: 180 },
      {
        field: "status",
        headerName: t("dcShared:status"),
        width: 190,
        sortable: false,
        renderCell: () => <Chip size="small" color="info" label={t("dcShared:sentAwaitingConfirmation")} />,
      },
      { field: "orderCount", headerName: t("dcShared:orders"), width: 90 },
      { field: "amount", headerName: t("dcShared:amount"), width: 140, valueFormatter: (v) => formatCurrency(v) },
      {
        field: "discountAmount",
        headerName: t("dcShared:couponDiscount"),
        width: 150,
        valueFormatter: (v) => (v > 0 ? formatCurrency(v) : "—"),
      },
      { field: "sentAt", headerName: t("dcShared:sentAt"), width: 160, valueFormatter: (v) => formatDate(v) },
      { field: "note", headerName: t("dcShared:note"), flex: 1, minWidth: 150 },
    ],
    [t]
  );

  const historyColumns = useMemo(
    () => [
      { field: "storeName", headerName: t("dcShared:store"), flex: 1, minWidth: 180 },
      {
        field: "status",
        headerName: t("dcShared:status"),
        width: 150,
        sortable: false,
        renderCell: () => <Chip size="small" color="success" label={t("dcStoreFees:received")} />,
      },
      { field: "orderCount", headerName: t("dcShared:orders"), width: 90 },
      { field: "amount", headerName: t("dcShared:amount"), width: 140, valueFormatter: (v) => formatCurrency(v) },
      {
        field: "discountAmount",
        headerName: t("dcShared:couponDiscount"),
        width: 150,
        valueFormatter: (v) => (v > 0 ? formatCurrency(v) : "—"),
      },
      { field: "sentAt", headerName: t("dcShared:sentAt"), width: 160, valueFormatter: (v) => formatDate(v) },
      { field: "receivedAt", headerName: t("dcStoreFees:confirmedAt"), width: 160, valueFormatter: (v) => formatDate(v) },
      { field: "note", headerName: t("dcShared:note"), flex: 1, minWidth: 150 },
    ],
    [t]
  );

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 1 }}>
        {t("dcStoreFees:title")}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {t("dcStoreFees:description")}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {t(error)}
        </Alert>
      )}

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            icon={HourglassTopOutlinedIcon}
            label={t("dcStoreFees:statRequested")}
            value={formatCurrency(totals.requested)}
            color="warning"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            icon={AddCircleOutlineOutlinedIcon}
            label={t("dcStoreFees:statNew")}
            value={formatCurrency(totals.newPending)}
            color="info"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            icon={MarkEmailReadOutlinedIcon}
            label={t("dcShared:sentAwaitingConfirmation")}
            value={formatCurrency(totals.awaitingConfirmation)}
            color="secondary"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            icon={PaidOutlinedIcon}
            label={t("dcStoreFees:statTotalPaid")}
            value={formatCurrency(totals.totalPaid)}
            color="success"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            icon={LocalOfferOutlinedIcon}
            label={t("dcStoreFees:statDiscounts")}
            value={formatCurrency(totals.discountGiven)}
            color="error"
          />
        </Grid>
      </Grid>

      <Tabs value={view} onChange={(e, v) => setView(v)} sx={{ mb: 2 }}>
        {VIEW_TABS.map((tab) => (
          <Tab key={tab.value} value={tab.value} label={t(tab.labelKey)} />
        ))}
      </Tabs>

      {view === "requests" && (
        <Box sx={{ bgcolor: "background.paper", borderRadius: 2 }}>
          <DataGrid
            autoHeight
            rows={requestRows}
            getRowId={(row) => row.storeId}
            columns={requestColumns}
            loading={loading}
            hideFooter
            disableRowSelectionOnClick
            getRowHeight={() => "auto"}
            sx={{ border: "none" }}
          />
          {!loading && requestRows.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ p: 3 }}>
              {t("dcStoreFees:emptyRequests")}
            </Typography>
          )}
        </Box>
      )}

      {view === "awaiting" && (
        <Box sx={{ bgcolor: "background.paper", borderRadius: 2 }}>
          <DataGrid
            autoHeight
            rows={awaitingRows}
            getRowId={(row) => row.storePayoutId}
            columns={awaitingColumns}
            loading={loading}
            hideFooter
            disableRowSelectionOnClick
            sx={{ border: "none" }}
          />
          {!loading && awaitingRows.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ p: 3 }}>
              {t("dcStoreFees:emptyAwaiting")}
            </Typography>
          )}
        </Box>
      )}

      {view === "history" && (
        <Box sx={{ bgcolor: "background.paper", borderRadius: 2 }}>
          <DataGrid
            autoHeight
            rows={completedRows}
            getRowId={(row) => row.storePayoutId}
            columns={historyColumns}
            loading={loading}
            hideFooter
            disableRowSelectionOnClick
            sx={{ border: "none" }}
          />
          {!loading && completedRows.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ p: 3 }}>
              {t("dcStoreFees:emptyHistory")}
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
}
